import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Modal, Form, Input, Select, message, Space, Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { categoriaService } from '../../services/api';

const { Option } = Select;

const Categorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [form] = Form.useForm();

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await categoriaService.getAll();
      setCategorias(res.data || []);
    } catch {
      message.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const abrirNueva = () => {
    setEditando(null);
    form.resetFields();
    form.setFieldsValue({ sexo: 'F', visualizacion: 1 });
    setModalOpen(true);
  };

  const abrirEditar = (record) => {
    setEditando(record);
    form.setFieldsValue({ nom_categoria: record.nom_categoria, sexo: record.sexo || 'F', visualizacion: record.visualizacion || 1 });
    setModalOpen(true);
  };

  const cerrar = () => { setModalOpen(false); setEditando(null); form.resetFields(); };

  const guardar = async (values) => {
    setGuardando(true);
    try {
      if (editando) {
        await categoriaService.update(editando.id_categoria, values);
        message.success('Categoría actualizada');
      } else {
        await categoriaService.create(values);
        message.success('Categoría creada');
      }
      cerrar();
      cargar();
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id) => {
    try {
      await categoriaService.delete(id);
      message.success('Categoría eliminada');
      cargar();
    } catch (err) {
      message.error(err.response?.data?.error || 'No se puede eliminar');
    }
  };

  const columnas = [
    {
      title: 'Categoría',
      dataIndex: 'nom_categoria',
      key: 'nom_categoria',
      sorter: (a, b) => a.nom_categoria.localeCompare(b.nom_categoria),
    },
    {
      title: 'Sexo',
      dataIndex: 'sexo',
      key: 'sexo',
      width: 100,
      align: 'center',
      filters: [
        { text: 'Macho', value: 'M' },
        { text: 'Hembra', value: 'F' },
      ],
      onFilter: (value, record) => record.sexo === value,
      render: (v) => (
        <Tag color={v === 'M' ? 'blue' : 'pink'}>
          {v === 'M' ? '♂ Macho' : '♀ Hembra'}
        </Tag>
      ),
    },
    {
      title: 'Visualización',
      dataIndex: 'visualizacion',
      key: 'visualizacion',
      width: 200,
      render: (v) => {
        const etiquetas = {
          1: { label: 'Toro', color: 'blue' },
          2: { label: 'Hembra c/servicio', color: 'purple' },
          3: { label: 'Hembra c/cría', color: 'green' },
          4: { label: 'Hembra básica', color: 'default' },
          5: { label: 'Parida c/servicio', color: 'orange' },
        };
        const e = etiquetas[v] || { label: `Tipo ${v}`, color: 'default' };
        return <Tag color={e.color}>{e.label}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      align: 'center',
      width: 120,
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => abrirEditar(r)}
          />
          <Popconfirm
            title="¿Eliminar esta categoría?"
            description="Solo se puede eliminar si no tiene animales asociados."
            onConfirm={() => eliminar(r.id_categoria)}
            okText="Sí"
            cancelText="No"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Categorías de animales</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirNueva}>
          Nueva categoría
        </Button>
      </div>

      <Table
        scroll={{ x: 'max-content' }}
        rowKey="id_categoria"
        dataSource={categorias}
        columns={columnas}
        loading={loading}
        pagination={false}
        size="middle"
      />

      <Modal
        title={editando ? 'Editar categoría' : 'Nueva categoría'}
        open={modalOpen}
        onCancel={cerrar}
        footer={null}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={guardar} style={{ marginTop: 12 }}>
          <Form.Item
            name="nom_categoria"
            label="Nombre"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Input placeholder="Ej: Vaquillona Sin Servicio" />
          </Form.Item>

          <Form.Item
            name="sexo"
            label="Sexo"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Select>
              <Option value="M">♂ Macho</Option>
              <Option value="F">♀ Hembra</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="visualizacion"
            label="Tipo de visualización"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Select>
              <Option value={1}>🐂 Toro</Option>
              <Option value={2}>🐄 Hembra con servicio</Option>
              <Option value={3}>🐄 Hembra con cría</Option>
              <Option value={4}>🐄 Hembra básica</Option>
              <Option value={5}>🐄 Hembra parida con servicio</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={cerrar} style={{ marginRight: 8 }}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={guardando}>
              {editando ? 'Guardar cambios' : 'Crear'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Categorias;
